<template>
  <el-dialog title="自定义指标" :visible.sync="visible" :close-on-click-modal="false" width="1280px" class="custom-column-dialog">
    <div class="custom-column-container">
      <!-- 搜索框 -->
      <div class="search-box">
        <el-input v-model="searchValue" style="width: 210px" clearable placeholder="请输入名称搜索"></el-input>
        <el-button @click="resetCols" type="text">重置自定义列</el-button>
      </div>

      <div class="content-wrapper">
        <!-- 左侧树形菜单 -->
        <div class="left-menu-tree" v-if="navs && navs.length > 0">
          <el-menu :default-openeds="navs.map((nav) => nav.key)" class="indicator-menu" @select="handleMenuSelect">
            <sub-menu v-for="nav in navs" :key="nav.key" :route="nav"></sub-menu>
          </el-menu>
        </div>

        <!-- 中间选择区 -->
        <div class="middle-content">
          <div class="indicator-section" v-for="nav in navs" :key="nav.key">
            <template v-for="childNav in nav.children">
              <div class="section-item" :key="childNav.key" v-if="getColsByDimension(childNav.key).length">
                <div class="section-title">
                  <el-checkbox v-model="navCheckAll[childNav.key]" @change="handleNavCheckAllChange($event, childNav.key)"></el-checkbox>
                  <span v-if="navs && navs.length"> {{ nav.label }}-{{ childNav.label }}</span>
                  <span v-else>全选</span>
                </div>

                <div class="check-box-wrapper">
                  <el-checkbox
                    v-model="col.checked"
                    v-for="col in getColsByDimension(childNav.key)"
                    :disabled="col.disabled"
                    :key="col.key"
                    :label="col.key"
                  >
                    {{ col.title }}
                  </el-checkbox>
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- 右侧已选 -->
        <div class="right-selected">
          <div class="selected-header">
            <span>已选{{ selectedCount }}个</span>
            <el-button type="text" @click="clearAll">清空全部</el-button>
          </div>

          <draggable v-model="sortableList" class="selected-list" handle=".drag-handle">
            <div v-for="item in sortableList" :key="item.value" class="selected-item">
              <i class="el-icon-rank drag-handle"></i>
              <span class="item-name">{{ item.title }}</span>
              <i class="el-icon-close" v-if="!item.disabled" @click="removeItem(item)"></i>
            </div>
          </draggable>
        </div>
      </div>

      <!-- 底部操作栏 -->
      <div class="dialog-footer">
        <div v-if="modifyState">
          <el-input v-model="modifyName" clearable placeholder="请输入模版名称" show-word-limit maxlength="20" class="template-input">
          </el-input>
        </div>
        <div class="save-template" v-else>
          <el-checkbox v-model="saveAsTemplate">保存为模版</el-checkbox>
          <el-input
            v-if="saveAsTemplate"
            v-model="templateName"
            clearable
            placeholder="请输入模版名称"
            show-word-limit
            maxlength="20"
            class="template-input"
          >
          </el-input>
        </div>
        <div class="footer-buttons">
          <el-button @click="handleClose">取消</el-button>
          <el-button type="primary" v-if="modifyState" @click="handleConfirm">更新模板</el-button>
          <el-button type="primary" v-else @click="handleConfirm">确定</el-button>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue'
import draggable from 'vuedraggable'
import { MessageBox } from 'element-ui'
import SubMenu from './components/sub-menu.vue'
import {
  useCustomTableMenu,
  useCustomTableColSave,
  useCustomTableColSelect,
  useCustomTableColRemove,
  useCustomTableColLocalStorage
} from './hooks/useCustomTableCols.js'
import { setCustomStorage } from './utils.js'

// eslint-disable-next-line
const props = defineProps({
  visible: {
    type: Boolean
  },
  // 本地缓存的key
  name: {
    type: String,
    required: true
  },
  // 默认配置的json表格列数据
  rawData: {
    type: Object,
    required: true
  },

  // 左侧导航数据
  navs: {
    type: Array,
    default: () => []
  }
})
// eslint-disable-next-line
const emit = defineEmits(['update:visible', 'change'])

const visible = computed({
  get: () => props.visible,
  set: (v) => {
    emit('update:visible', v)
  }
})
const searchValue = ref('')
const tableCols = ref([])
const { handleMenuSelect } = useCustomTableMenu(props, tableCols)
const { getTableCols, updateTableColWidth } = useCustomTableColLocalStorage(props, tableCols)
const { sortarr, navCheckAll, sortableList, selectedList, selectedCount, handleNavCheckAllChange } = useCustomTableColSelect(
  props,
  tableCols
)
const { modifyState, modifyName, saveAsTemplate, templateName, confirmAction } = useCustomTableColSave(selectedList, emit)
const { removeItem, clearAll } = useCustomTableColRemove(tableCols)

const tableListCols = computed(() => {
  const text = searchValue.value.trim().toLowerCase()
  if (text) {
    return tableCols.value.filter((col) => col.title.toLowerCase().includes(text))
  }
  return tableCols.value
})

// 加载组件默认调用一次，外部表格根据结果显示列
confirmAction()

function makeLocalFormatCols() {
  const colsArr = sortableList.value
    .filter((col) => col.checked)
    .map((col) => {
      const width = Number(col.width)
      if (!width || Object.is(width, NaN)) return col.key
      return `${col.key}|${width}`
    })

  return colsArr
}

async function handleConfirm() {
  try {
    const formatedArr = makeLocalFormatCols()
    await confirmAction(formatedArr)
    setCustomStorage(props.name, formatedArr)
    handleClose()
  } catch (error) {
    console.log('eeee output: ', error)
  }
}
function resetCols() {
  MessageBox.confirm('确认重置自定义表格列？', '确认', { type: 'warning' }).then(() => {
    setCustomStorage(props.name, null)
    tableCols.value = []
    nextTick(() => {
      tableCols.value = getTableCols()
    })
  })
}

function handleColsModify({ cols, name }) {
  modifyState.value = true
  modifyName.value = name
  tableCols.value = getTableCols(cols)
  sortarr.value = tableCols.value.map((col) => col.key)
}

function handleColsTemplateSelect(cols) {
  tableCols.value = getTableCols(cols)
  sortarr.value = tableCols.value.map((col) => col.key)
  handleConfirm()
}

/**
 * @description: 分维度获取列
 * @param {*} dim
 * @return {*}
 */
function getColsByDimension(dim) {
  return tableListCols.value.filter((col) => col.dim === dim)
}

function handleClose() {
  visible.value = false
  modifyState.value = false

  templateName.value = ''
  saveAsTemplate.value = false

  const isCheckAll = {}
  for (const i in props.rawData) {
    isCheckAll[i] = false
  }

  navCheckAll.value = isCheckAll
}

// eslint-disable-next-line
defineExpose({
  updateTableColWidth: (newWidth, column) => {
    const colsArr = makeLocalFormatCols()
    updateTableColWidth(colsArr, newWidth, column)
  },
  // 外部传入表格列信息，显示对应的勾选情况，主要是用于表格列模板编辑
  handleColsModify,
  handleColsTemplateSelect
})
</script>

<style lang="scss" scoped>
.custom-column-dialog {
  .custom-column-container {
    display: flex;
    flex-direction: column;
    height: 600px;
  }

  .search-box {
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .content-wrapper {
    display: flex;
    flex: 1;
    border: 1px solid #e6e6e6;
    margin-bottom: 20px;
    border-radius: 4px;
    overflow: hidden;
  }

  ::v-deep .left-menu-tree {
    width: 210px;
    border-right: 1px solid #e6e6e6;
    overflow-y: auto;
    .el-menu {
      border-right: none;

      .el-submenu__icon-arrow {
        display: none;
      }
    }
  }

  .middle-content {
    flex: 1;
    padding: 10px;
    overflow-y: auto;
    height: 506px;

    .indicator-section {
      .section-item {
        margin-bottom: 20px;
      }
      .section-title {
        font-weight: bold;
        margin-bottom: 10px;
        padding: 5px;
        background-color: #f5f7fa;
      }

      .check-box-wrapper {
        padding: 5px;
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 5px;
      }
    }
  }

  .right-selected {
    width: 250px;
    border-left: 1px solid #e6e6e6;
    display: flex;
    flex-direction: column;

    .selected-header {
      padding: 2px 10px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 1px solid #e6e6e6;
      background-color: #f5f7fa;
    }

    .selected-list {
      font-size: 12px;
      overflow-y: auto;
      padding: 10px;
      height: 500px;
    }

    .selected-item {
      display: flex;
      align-items: center;
      padding: 5px;
      margin-bottom: 5px;
      background: #f5f7fa;
      border-radius: 4px;

      .drag-handle {
        cursor: move;
        margin-right: 8px;
      }

      .item-name {
        flex: 1;
      }

      .el-icon-close {
        cursor: pointer;
        &:hover {
          color: #f56c6c;
        }
      }
    }
  }

  .dialog-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .save-template {
      display: flex;
      align-items: center;
    }

    .template-input {
      width: 380px;
      margin-left: 10px;
    }
  }
}
</style>
